import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { listThemes } from "../../http/themes";

const isValidMediaId = (id) =>
  typeof id === "string" && /^[0-9a-fA-F]{24}$/.test(id);

export const stickerMediaId = (sticker) => {
  const raw = sticker?.mediaId?._id || sticker?.mediaId || sticker?._id || "";
  return raw ? String(raw) : "";
};

/** Append one library sticker, skipping duplicates and anything past `max`. */
export function appendLibrarySticker(stickers, sticker, max) {
  const mediaId = stickerMediaId(sticker);
  const url = sticker?.url || "";
  if (!isValidMediaId(mediaId) || !url) return stickers;
  if (stickers.length >= max) return stickers;
  if (
    stickers.some((item) => item.mediaId === mediaId || item.url === url)
  ) {
    return stickers;
  }
  return [...stickers, { mediaId, url }];
}

/**
 * Pick stickers that were already uploaded to a theme's library, instead of
 * uploading the same file again.
 */
export default function ThemeStickerPicker({
  stickers = [],
  maxStickers,
  onPick,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [themes, setThemes] = useState([]);
  const [selectedThemeId, setSelectedThemeId] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setLoadError("");
      try {
        const response = await listThemes({
          page: 1,
          limit: 50,
          search: search.trim() || undefined,
        });
        if (cancelled) return;
        const list = (response?.data?.list || []).filter(
          (theme) => (theme.stickers || []).some((sticker) => sticker?.url)
        );
        setThemes(list);
        setSelectedThemeId((current) =>
          list.some((theme) => theme._id === current)
            ? current
            : list[0]?._id || ""
        );
      } catch (error) {
        if (!cancelled) {
          setThemes([]);
          setLoadError(
            error.response?.data?.message || "Could not load theme libraries."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, search]);

  const selectedTheme = themes.find((theme) => theme._id === selectedThemeId);
  const library = selectedTheme?.stickers || [];
  const atMax = stickers.length >= maxStickers;

  const isAlreadyAdded = (sticker) => {
    const mediaId = stickerMediaId(sticker);
    return stickers.some(
      (item) =>
        (mediaId && item.mediaId === mediaId) || item.url === sticker.url
    );
  };

  return (
    <>
      <Button
        size="small"
        disabled={disabled || atMax}
        onClick={() => setOpen(true)}
        sx={{
          mt: 1,
          textTransform: "none",
          color: "#142C5C",
          padding: 0,
          minWidth: 0,
        }}
      >
        Add from theme library
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Theme sticker library
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
            Pick stickers already stored on a theme. Save the theme first if
            you just uploaded them. {stickers.length} of {maxStickers} added.
          </Typography>
          <input
            placeholder="Search themes..."
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="search-field-ui"
            style={{ height: "40px", width: "100%", marginBottom: 12 }}
          />
          {loading && (
            <Typography variant="caption" color="text.secondary">
              Loading themes...
            </Typography>
          )}
          {loadError && (
            <Typography variant="caption" color="error">
              {loadError}
            </Typography>
          )}
          {!loading && !loadError && themes.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No themes with stickers yet.
            </Typography>
          )}
          {themes.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
              {themes.map((theme) => {
                const selected = theme._id === selectedThemeId;
                return (
                  <Button
                    key={theme._id}
                    size="small"
                    variant={selected ? "contained" : "outlined"}
                    onClick={() => setSelectedThemeId(theme._id)}
                    sx={{
                      textTransform: "none",
                      ...(selected && {
                        backgroundColor: "#142C5C",
                        "&:hover": { backgroundColor: "#0f2247" },
                      }),
                    }}
                  >
                    {theme.title} ({theme.stickers.length})
                  </Button>
                );
              })}
            </Box>
          )}
          <Box className="sticker-preview-row">
            {library.map((sticker) => {
              const added = isAlreadyAdded(sticker);
              const mediaId = stickerMediaId(sticker);
              return (
                <Box
                  key={mediaId || sticker.url}
                  component="button"
                  type="button"
                  disabled={added || atMax || !isValidMediaId(mediaId)}
                  onClick={() =>
                    onPick({ mediaId, url: sticker.url })
                  }
                  title={added ? "Already added" : "Add sticker"}
                  sx={{
                    width: 64,
                    height: 64,
                    p: 0.5,
                    borderRadius: "8px",
                    border: added ? "2px solid #142C5C" : "1px solid #ddd",
                    background: added ? "#eef2f8" : "#fff",
                    cursor: added || atMax ? "default" : "pointer",
                    opacity: atMax && !added ? 0.45 : 1,
                  }}
                >
                  <img
                    src={sticker.url}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              onClick={() => setOpen(false)}
              sx={{ textTransform: "none" }}
            >
              Done
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
